import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  emptyStore,
  initialsOf,
  maskEmail,
  maskPhone,
  roleAccessState,
  firstNameOf,
  type Application,
  type EmployerAccount,
  type EmployerJob,
  type EmployerStore,
  type RoleAccessState,
} from "@/components/site/employer";
import {
  deleteEmployerJob,
  employerLogout,
  fetchEmployerWorkspace,
  getEmployerSession,
  markApplicationViewed,
  updateApplicationStatus,
  updateEmployerJob,
  updateEmployerPassword,
  updateEmployerProfile,
} from "@/lib/employer-api";
import { CandidateDatabase } from "@/components/site/CandidateDatabase";
import { PasswordInput } from "@/components/site/PasswordInput";
import { AccessPill, CandidateAccessBanner, UnlockCandidateCta } from "@/components/site/RoleAccess";
import { assessmentPrompt, assessmentQuestions } from "@/components/site/showcase";

export const Route = createFileRoute("/employer-preview")({
  head: () => {
    const t = "Your applications | CLOSDD";
    const d =
      "See the sales people who applied to the role CLOSDD is running for you, listen to their pitches and unlock full profiles when you are ready.";
    return {
      meta: [
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "noindex" },
      ],
    };
  },
  component: SourcedConsole,
});

type Tab = "roles" | "shortlist" | "database" | "account";
type JobTab = "advert" | "candidates";

const labelClass = "text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/45";
const panelClass =
  "rounded-2xl border border-purple-strong/[0.07] bg-white shadow-[0_1px_2px_rgba(13,27,46,0.04),0_18px_44px_-28px_rgba(13,27,46,0.18)]";
const btnClass =
  "inline-flex items-center justify-center rounded-xl bg-purple-strong px-4 py-2.5 text-[13px] font-semibold tracking-[0.01em] text-white transition-all hover:bg-purple-mid hover:shadow-[0_12px_26px_-14px_rgba(13,27,46,0.55)]";
const ghostClass =
  "inline-flex items-center justify-center rounded-xl border border-purple-strong/12 bg-white px-4 py-2.5 text-[13px] font-semibold text-ink/75 transition hover:border-purple-strong/40 hover:text-purple-strong";
const dangerClass =
  "inline-flex items-center justify-center rounded-xl border border-purple-strong/12 bg-white px-4 py-2.5 text-[13px] font-semibold text-ink/50 transition hover:border-tag-red/40 hover:text-tag-red";
const inputClass =
  "w-full rounded-xl border border-purple-strong/12 bg-white px-3.5 py-2.5 text-[15px] text-ink outline-none transition focus:border-purple-mid focus:ring-2 focus:ring-purple-mid/15";
const areaClass =
  "w-full rounded-xl border border-purple-strong/12 bg-white px-3.5 py-3 text-[15px] leading-[1.65] text-ink outline-none transition focus:border-purple-mid focus:ring-2 focus:ring-purple-mid/15";

/** Mirrors the real header + sidebar + panel shell so the dashboard doesn't flash blank while the workspace fetch is in flight. */
function EmployerSkeleton() {
  const bar = "animate-pulse rounded-xl bg-ink/10";
  return (
    <div className="min-h-screen bg-[#f6f8fc]">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-purple-strong">
        <div className="mx-auto flex w-full max-w-[1240px] items-center justify-between gap-6 px-5 py-3.5">
          <div className="flex items-center gap-4">
            <span className="font-serif text-[16px] font-bold tracking-[-0.01em] text-white">CLOSDD</span>
            <span className="hidden h-5 w-px bg-white/20 sm:block" />
            <span className="hidden text-[12px] font-semibold uppercase tracking-[0.16em] text-white/55 sm:block">
              Employer console
            </span>
          </div>
          <div className="h-8 w-24 animate-pulse rounded-md bg-white/15" />
        </div>
      </header>

      <div className="mx-auto grid w-full max-w-[1240px] gap-7 px-5 py-7 lg:grid-cols-[248px_1fr] lg:items-start">
        <aside className={`${panelClass} overflow-hidden p-4`}>
          <div className="grid gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`h-9 ${bar}`} />
            ))}
          </div>
        </aside>
        <main className="grid gap-5">
          <div className="grid gap-4 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`h-24 ${panelClass} ${bar} bg-white/60`} />
            ))}
          </div>
          <div className={`h-72 ${panelClass} ${bar} bg-white/60`} />
        </main>
      </div>
    </div>
  );
}

function SourcedConsole() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("roles");
  const [store, setStore] = useState<EmployerStore>(emptyStore);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [actionError, setActionError] = useState("");
  const [loggingOut, setLoggingOut] = useState(false);
  const [viewJob, setViewJob] = useState<string | null>(null);
  const [jobTab, setJobTab] = useState<JobTab>("advert");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = await getEmployerSession();
      if (!user) {
        if (!cancelled) navigate({ to: "/login" });
        return;
      }
      try {
        const s = await fetchEmployerWorkspace();
        if (!cancelled) {
          setStore(s);
          setReady(true);
          setLoadError("");
        }
      } catch (err) {
        // A failed workspace fetch does not mean the session is invalid —
        // getEmployerSession() already confirmed that above. Redirecting to
        // /login here was signing employers out of a perfectly good session
        // on any transient error; show a retry instead.
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "We couldn't load your dashboard. Try again.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, retryCount]);

  /** Runs a mutation against the API, surfacing any failure as a banner instead of silently doing nothing. */
  async function act(run: () => Promise<void>) {
    setActionError("");
    try {
      await run();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    }
  }

  async function handleStatus(id: string, status: Application["status"]) {
    await act(async () => {
      const updated = await updateApplicationStatus(id, status);
      setStore((s) => ({ ...s, applications: s.applications.map((a) => (a.id === id ? updated : a)) }));
    });
  }

  async function handleJobChange(next: EmployerJob) {
    await act(async () => {
      const updated = await updateEmployerJob(next.id, {
        ref: next.ref,
        title: next.title,
        seniority: next.seniority,
        contract: next.contract,
        company: next.company,
        location: next.location,
        setting: next.setting,
        payStructure: next.payStructure,
        pay: next.pay,
        advert: next.advert,
        status: next.status,
      });
      setStore((s) => ({ ...s, jobs: s.jobs.map((j) => (j.id === next.id ? updated : j)) }));
    });
  }

  async function handleJobRemove(id: string) {
    await act(async () => {
      await deleteEmployerJob(id);
      setStore((s) => ({
        ...s,
        jobs: s.jobs.filter((j) => j.id !== id),
        applications: s.applications.filter((a) => a.jobId !== id),
      }));
      setViewJob(null);
    });
  }

  async function handleView(jobId: string, appId: string) {
    await act(async () => {
      const access = await markApplicationViewed(jobId, appId);
      setStore((s) => ({ ...s, access: { ...s.access, [jobId]: access } }));
    });
  }

  async function handleAccountSave(a: EmployerAccount) {
    await act(async () => {
      const updated = await updateEmployerProfile({
        company: a.company,
        contactName: a.contactName,
        email: a.email,
        phone: a.phone,
      });
      setStore((s) => ({ ...s, account: updated }));
    });
  }

  if (loadError) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f6f8fc] px-5">
        <div className="grid max-w-[420px] gap-4 text-center">
          <p className="text-[15px] text-ink/70">{loadError}</p>
          <button
            type="button"
            onClick={() => setRetryCount((n) => n + 1)}
            className="mx-auto rounded-full bg-purple-strong px-6 py-3 text-[13px] font-bold uppercase tracking-[0.14em] text-white transition hover:bg-purple-mid"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!ready || !store.account) return <EmployerSkeleton />;

  const account = store.account;
  const jobs = store.jobs;
  const accessOf = (jobId: string) => roleAccessState(store, jobId);
  const activeJobs = jobs.filter((j) => j.status === "Live" && (j.decision === undefined || j.decision === "Approved"));
  // /employer/applications already only returns applications the CLOSDD team has approved.
  const visibleApps = store.applications;
  const totalApps = visibleApps.length;
  // The role we nudge at the top: the one with applications that is still locked.
  const focusJob =
    jobs.find((j) => !accessOf(j.id).unlocked && visibleApps.some((a) => a.jobId === j.id)) ??
    jobs.find((j) => !accessOf(j.id).unlocked);

  return (
    <div className="min-h-screen bg-[#f6f8fc]">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-purple-strong">
        <div className="mx-auto flex w-full max-w-[1240px] items-center justify-between gap-6 px-5 py-3.5">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-serif text-[16px] font-bold tracking-[-0.01em] text-white">
              CLOSDD
            </Link>
            <span className="hidden h-5 w-px bg-white/20 sm:block" />
            <span className="hidden text-[12px] font-semibold uppercase tracking-[0.16em] text-white/55 sm:block">
              Your applications
            </span>
          </div>
          <div className="flex items-center gap-3">
            {focusJob ? null : jobs.length ? (
              <span className="hidden rounded border border-white/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white/70 sm:block">
                Candidates unlocked
              </span>
            ) : null}
            <Link to="/post-job" className="rounded-md bg-white px-4 py-2 text-[13px] font-semibold text-purple-strong transition hover:bg-purple-soft">
              Post a role
            </Link>
            <button
              type="button"
              disabled={loggingOut}
              onClick={() => {
                setLoggingOut(true);
                void employerLogout();
                navigate({ to: "/login" });
              }}
              className="rounded-md border border-white/20 px-4 py-2 text-[13px] font-semibold text-white/70 transition hover:border-white/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loggingOut ? "Logging out…" : "Log out"}
            </button>
          </div>
        </div>
      </header>

      {actionError ? (
        <div className="mx-auto w-full max-w-[1240px] px-5 pt-7">
          <div className="rounded-2xl border border-tag-red/25 bg-tag-red-soft px-6 py-4 text-[14px] font-semibold text-tag-red">
            {actionError}
          </div>
        </div>
      ) : null}

      {focusJob ? (
        <div className="mx-auto w-full max-w-[1240px] px-5 pt-7">
          <CandidateAccessBanner
            state={accessOf(focusJob.id)}
            jobId={focusJob.id}
            applicants={visibleApps.filter((a) => a.jobId === focusJob.id).length}
            showCta={false}
          />
        </div>
      ) : null}

      <div className="mx-auto grid w-full max-w-[1240px] gap-7 px-5 py-7 lg:grid-cols-[248px_1fr] lg:items-start">
        <aside className={`${panelClass} p-5 lg:sticky lg:top-[76px]`}>
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-purple-strong text-[14px] font-bold text-white">
              {initialsOf(account.company || account.contactName)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-[14px] font-semibold text-ink">
                {account.company || account.contactName || "Your account"}
              </p>
              <p className="truncate text-[12px] text-ink/50">{account.email}</p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2 border-y border-purple-strong/10 py-4">
            <div>
              <p className="font-serif text-[18px] leading-none text-ink">{activeJobs.length}</p>
              <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45">Live roles</p>
            </div>
            <div>
              <p className="font-serif text-[18px] leading-none text-ink">{totalApps}</p>
              <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45">Applications</p>
            </div>
          </div>

          <nav className="mt-4 grid gap-1">
            {([
              { id: "roles", label: "My Roles" },
              { id: "shortlist", label: "Shortlist" },
              { id: "database", label: "Access database" },
              { id: "account", label: "Account" },
            ] as { id: Tab; label: string }[]).map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setTab(item.id);
                  setViewJob(null);
                }}
                className={`rounded-md px-3.5 py-2.5 text-left text-[14px] font-semibold transition ${
                  tab === item.id
                    ? "bg-purple-soft text-purple-strong"
                    : "text-ink/60 hover:bg-ink/[0.04] hover:text-ink"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="mt-5 border-t border-purple-strong/10 pt-4">
            <p className="text-[12px] leading-relaxed text-ink/45">
              Need help?{" "}
              <a
                href="mailto:info@closdd.com"
                className="font-medium text-purple-strong underline-offset-2 hover:underline"
              >
                info@closdd.com
              </a>
            </p>
          </div>
        </aside>

        <main className="grid gap-6">
          {tab === "roles" && viewJob ? (
            <JobDetail
              job={jobs.find((j) => j.id === viewJob)}
              apps={visibleApps.filter((a) => a.jobId === viewJob)}
              access={accessOf(viewJob)}
              activeTab={jobTab}
              setActiveTab={setJobTab}
              onBack={() => setViewJob(null)}
              onView={(appId) => handleView(viewJob, appId)}
              onStatus={handleStatus}
              onJobChange={handleJobChange}
              onJobRemove={handleJobRemove}
            />
          ) : tab === "roles" ? (
            <RolesList
              jobs={activeJobs}
              countFor={(id) => visibleApps.filter((a) => a.jobId === id).length}
              newFor={(id) => visibleApps.filter((a) => a.jobId === id && a.status === "New").length}
              accessFor={accessOf}
              onOpen={(id) => {
                setViewJob(id);
                setJobTab("advert");
              }}
              onOpenApps={(id) => {
                setViewJob(id);
                setJobTab("candidates");
              }}
            />
          ) : tab === "shortlist" ? (
            <Shortlist
              jobs={jobs}
              apps={visibleApps.filter((a) => a.status === "Shortlisted")}
              accessFor={accessOf}
              onStatus={handleStatus}
            />
          ) : tab === "database" ? (
            <CandidateDatabase />
          ) : (
            <AccountPanel
              account={account}
              onSave={handleAccountSave}
              jobs={jobs}
              accessFor={accessOf}
              countFor={(id) => visibleApps.filter((a) => a.jobId === id).length}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function RolesList({
  jobs,
  countFor,
  newFor,
  accessFor,
  onOpen,
  onOpenApps,
}: {
  jobs: EmployerJob[];
  countFor: (id: string) => number;
  newFor: (id: string) => number;
  accessFor: (id: string) => RoleAccessState;
  onOpen: (id: string) => void;
  onOpenApps: (id: string) => void;
}) {
  return (
    <section className={panelClass}>
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-purple-strong/10 px-6 py-5">
        <div>
          <h2 className="font-serif text-[18px] tracking-[-0.01em] text-ink">My Roles</h2>
          <p className="mt-1 text-[14px] text-ink/55">
            {jobs.length} live {jobs.length === 1 ? "role" : "roles"} we are running for you
          </p>
        </div>
      </div>

      <div className="divide-y divide-ink/10">
        {jobs.length === 0 ? (
          <div className="px-6 py-12">
            <Empty text="Your role is being set live. Applications will appear here." />
          </div>
        ) : null}
        {jobs.map((job) => {
          const total = countFor(job.id);
          const fresh = newFor(job.id);
          const access = accessFor(job.id);
          return (
            <div key={job.id} className="px-6 py-5 transition hover:bg-purple-soft/25">
              <div className="flex flex-wrap items-start justify-between gap-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h3 className="text-left font-serif text-[16px] tracking-[-0.01em] text-ink">
                      {job.title}
                    </h3>
                    <button
                      type="button"
                      onClick={() => onOpen(job.id)}
                      className="text-[12px] font-semibold uppercase tracking-[0.12em] text-purple-strong underline-offset-4 transition hover:text-purple-mid hover:underline"
                    >
                      View role
                    </button>
                    <StatusPill status={jobStatusLabel(job)} />
                    {fresh ? <StatusPill status={`${fresh} new`} /> : null}
                    <AccessPill state={access} />
                  </div>
                  {job.ref ? (
                    <p className="mt-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-ink/45">
                      Ref {job.ref}
                    </p>
                  ) : null}
                  <p className="mt-1.5 text-[14px] text-ink/55">
                    {job.location} · {job.setting} · {job.contract}
                  </p>
                  <p className="mt-1 text-[14px] font-semibold text-ink/75">{job.pay}</p>
                  {!access.unlocked ? (
                    <p className="mt-1.5 text-[13px] text-ink/50">{access.label}</p>
                  ) : null}
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="font-serif text-[20px] leading-none text-purple-strong">{total}</p>
                    <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45">
                      Applicant{total === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => onOpenApps(job.id)} className={btnClass}>
                      View applications
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function JobDetail({
  job,
  apps,
  access,
  activeTab,
  setActiveTab,
  onBack,
  onView,
  onStatus,
  onJobChange,
  onJobRemove,
}: {
  job: EmployerJob | undefined;
  apps: Application[];
  access: RoleAccessState;
  activeTab: JobTab;
  setActiveTab: (t: JobTab) => void;
  onBack: () => void;
  onView: (appId: string) => void;
  onStatus: (id: string, status: Application["status"]) => void;
  onJobChange: (job: EmployerJob) => void;
  onJobRemove: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; value: string }>({ open: false, value: "" });

  if (!job) return null;
  const pro = access.unlocked;

  return (
    <div className="grid gap-6">
      <CandidateAccessBanner state={access} jobId={job.id} applicants={apps.length} />
    <section className={panelClass}>
      <div className="border-b border-purple-strong/10 px-6 py-5">
        <button
          type="button"
          onClick={onBack}
          className="text-[12px] font-semibold uppercase tracking-[0.14em] text-purple-strong transition hover:text-purple-mid"
        >
          Back to My Roles
        </button>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="font-serif text-[20px] leading-[1.1] tracking-[-0.015em] text-ink">{job.title}</h2>
              <StatusPill status={jobStatusLabel(job)} />
              <AccessPill state={access} />
            </div>
            <p className="mt-1.5 text-[14px] text-ink/55">
              {job.location} · {job.setting} · {job.contract}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onJobChange({ ...job, status: job.status === "Live" ? "Paused" : "Live" })}
              className={ghostClass}
            >
              {job.status === "Live" ? "Pause role" : "Set live"}
            </button>
            <button type="button" onClick={() => setEditing((v) => !v)} className={ghostClass}>
              {editing ? "Close editor" : "Edit advert"}
            </button>
            <button type="button" onClick={() => setDeleteConfirm({ open: true, value: "" })} className={dangerClass}>
              Delete
            </button>
          </div>
        </div>

        {editing ? (
          <EditJob
            job={job}
            onSave={(next) => {
              onJobChange(next);
              setEditing(false);
            }}
          />
        ) : null}
      </div>

      <div className="divide-y divide-ink/10">
        {apps.length === 0 ? (
          <div className="px-6 py-12">
            <Empty text="No approved applications for this role yet. We release them as soon as our team has reviewed them." />
          </div>
        ) : null}
        {apps.map((a) => (
          <ApplicationRow key={a.id} app={a} jobId={job.id} pro={pro} onView={onView} onStatus={onStatus} />
        ))}
      </div>

      <TypeToConfirmModal
        open={deleteConfirm.open}
        title="Delete this role"
        message="This will permanently remove the role and every application attached to it. Type delete to confirm."
        confirmWord="delete"
        value={deleteConfirm.value}
        onChange={(value) => setDeleteConfirm({ ...deleteConfirm, value })}
        onCancel={() => setDeleteConfirm({ open: false, value: "" })}
        onConfirm={() => {
          setDeleteConfirm({ open: false, value: "" });
          onJobRemove(job.id);
        }}
      />
    </section>
    </div>
  );
}

function EditJob({ job, onSave }: { job: EmployerJob; onSave: (j: EmployerJob) => void }) {
  const [draft, setDraft] = useState<EmployerJob>(job);
  const set = (patch: Partial<EmployerJob>) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <form
      className="mt-6 grid gap-5 rounded-xl border border-purple-strong/10 bg-[#f6f8fc] p-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-1.5">
          <span className={labelClass}>Job title</span>
          <input required maxLength={100} value={draft.title} onChange={(e) => set({ title: e.target.value })} className={inputClass} />
        </label>
        <label className="grid gap-1.5">
          <span className={labelClass}>Company name</span>
          <input maxLength={100} value={draft.company} onChange={(e) => set({ company: e.target.value })} className={inputClass} />
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="grid gap-1.5">
          <span className={labelClass}>Location</span>
          <input maxLength={80} value={draft.location} onChange={(e) => set({ location: e.target.value })} className={inputClass} />
        </label>
        <label className="grid gap-1.5">
          <span className={labelClass}>Setting</span>
          <select value={draft.setting} onChange={(e) => set({ setting: e.target.value })} className={inputClass}>
            {["On-site", "Hybrid", "Remote"].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className={labelClass}>Contract</span>
          <select value={draft.contract} onChange={(e) => set({ contract: e.target.value })} className={inputClass}>
            {["Full-time", "Part-time", "Contract / freelance", "Internship"].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="grid gap-1.5">
        <span className={labelClass}>Pay</span>
        <input maxLength={160} value={draft.pay} onChange={(e) => set({ pay: e.target.value })} className={inputClass} />
      </label>
      <label className="grid gap-1.5">
        <span className={labelClass}>The advert</span>
        <textarea rows={6} value={draft.advert} onChange={(e) => set({ advert: e.target.value })} className={areaClass} />
      </label>
      <button type="submit" className={`${btnClass} w-fit`}>
        Save changes
      </button>
    </form>
  );
}

function TypeToConfirmModal({
  open,
  title,
  message,
  confirmWord,
  value,
  onChange,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmWord: string;
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  const matched = value.trim().toLowerCase() === confirmWord.toLowerCase();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-purple-strong/60" onClick={onCancel} aria-hidden />
      <div className="relative w-full max-w-[420px] rounded-xl border border-purple-strong/10 bg-white p-6 shadow-xl">
        <h3 className="font-serif text-[17px] tracking-[-0.01em] text-ink">{title}</h3>
        <p className="mt-2 text-[15px] leading-[1.55] text-ink/65">{message}</p>
        <label className="mt-5 grid gap-2">
          <span className={labelClass}>Type {confirmWord} to confirm</span>
          <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={confirmWord} className={inputClass} />
        </label>
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <button type="button" onClick={onCancel} className={ghostClass}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={!matched} className={`${dangerClass} disabled:opacity-40`}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Shortlist({
  jobs,
  apps,
  accessFor,
  onStatus,
}: {
  jobs: EmployerJob[];
  apps: Application[];
  accessFor: (id: string) => RoleAccessState;
  onStatus: (id: string, status: Application["status"]) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section className="grid gap-6">
      <div className={`${panelClass} px-6 py-5`}>
        <h2 className="font-serif text-[18px] tracking-[-0.01em] text-ink">Shortlist</h2>
        <p className="mt-1 text-[14px] text-ink/55">
          The people you have shortlisted, grouped under each role. Unlock a role to reach out to them directly.
        </p>
      </div>

      <div className={`${panelClass} divide-y divide-ink/10`}>
        {jobs.length === 0 ? <div className="px-6 py-12"><Empty text="No roles yet." /></div> : null}
        {jobs.map((job) => {
          const list = apps.filter((a) => a.jobId === job.id);
          const isOpen = open === job.id;
          const access = accessFor(job.id);
          return (
            <div key={job.id}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : job.id)}
                className="flex w-full flex-wrap items-center justify-between gap-4 px-6 py-4 text-left transition hover:bg-purple-soft/25"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className={`text-[13px] text-ink/40 transition-transform ${isOpen ? "rotate-90" : ""}`} aria-hidden>
                    ▶
                  </span>
                  <div className="min-w-0">
                    <p className="text-[16px] font-semibold text-ink">{job.title}</p>
                    <p className="mt-0.5 text-[13px] text-ink/50">{job.location}</p>
                  </div>
                  <AccessPill state={access} />
                </div>
                <span className="text-[13px] font-semibold text-ink/55">
                  {list.length === 0 ? "No shortlist yet" : `${list.length} shortlisted`}
                </span>
              </button>
              {isOpen ? (
                <div className="border-t border-purple-strong/10 bg-[#fbfcfe]">
                  {list.length === 0 ? (
                    <div className="px-6 py-8">
                      <Empty text="Nobody shortlisted for this role yet." />
                    </div>
                  ) : (
                    <div className="divide-y divide-ink/10">
                      {list.map((a) => (
                        <ApplicationRow
                          key={a.id}
                          app={a}
                          jobId={job.id}
                          pro={access.unlocked}
                          onView={() => {}}
                          onStatus={onStatus}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ApplicationRow({
  app,
  jobId,
  pro,
  onView,
  onStatus,
}: {
  app: Application;
  jobId: string;
  pro: boolean;
  onView: (appId: string) => void;
  onStatus: (id: string, status: Application["status"]) => void;
}) {
  const [open, setOpen] = useState(false);
  const shownName = pro ? app.name : firstNameOf(app.name);

  return (
    <div className="px-6 py-5 transition hover:bg-purple-soft/20">
      <div className="grid gap-4 lg:grid-cols-[minmax(210px,1fr)_280px_300px] lg:items-center">
        <div className="flex min-w-0 items-center gap-4">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-purple-soft text-[14px] font-bold text-purple-strong">
            {initialsOf(shownName)}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <p className="text-[16px] font-semibold text-ink">{shownName}</p>
              <StatusPill status={app.status} />
            </div>
            <p className="mt-0.5 text-[13px] text-ink/50">
              {app.years} · {app.location} · {app.appliedAt}
            </p>
            <p className="mt-1 text-[13px] text-ink/50">
              {pro ? app.email || "—" : maskEmail(app.email)} · {pro ? app.phone || "—" : maskPhone(app.phone)}
            </p>
          </div>
        </div>

        <PitchPlayer label="60-second pitch" available={app.hasPitch} url={app.audioUrl} className="w-full" />

        <div className="grid w-full grid-cols-[minmax(0,176px)_112px] justify-start gap-2 lg:justify-end">
          <button
            type="button"
            onClick={() => {
              if (!open) onView(app.id);
              setOpen((v) => !v);
            }}
            className="inline-flex h-11 w-full items-center justify-center rounded-xl border border-purple-strong/12 bg-white px-3 text-center text-[13px] font-semibold leading-[1.2] text-ink/75 transition hover:border-purple-strong/30 hover:text-ink"
          >
            {open ? "Close application" : "Open application"}
          </button>
          <button
            type="button"
            onClick={() => onStatus(app.id, app.status === "Shortlisted" ? "New" : "Shortlisted")}
            className={`${app.status === "Shortlisted" ? ghostClass : btnClass} h-11 w-full px-2`}
          >
            {app.status === "Shortlisted" ? "Shortlisted" : "Shortlist"}
          </button>
        </div>
      </div>

      {open ? (
        <div className="mt-5 rounded-xl border border-purple-strong/10 bg-[#f6f8fc] p-5">
          <div className="grid gap-5 lg:grid-cols-[340px_1fr] lg:items-start">
            <div className="grid gap-4">
              <div className="rounded-xl border border-purple-strong/10 bg-white p-4">
                <p className={labelClass}>Candidate details</p>
                <p className="mt-2 text-[16px] font-semibold text-ink">{shownName}</p>
                <div className="mt-3 grid gap-2 text-[14px]">
                  <Fact label="Location" value={app.location || "Not provided"} />
                  <Fact label="Experience" value={app.years || "Not provided"} />
                  <Fact label="Applied" value={app.appliedAt || "Recently"} />
                </div>
                <Locked locked={!pro} className="mt-3">
                  <div className="grid gap-2 text-[14px]">
                    <Fact
                      label="Full name"
                      value={pro ? app.name : "Hidden until you upgrade"}
                    />
                    <Fact
                      label="Email"
                      value={pro ? app.email || "Not provided" : maskEmail(app.email)}
                      href={pro && app.email ? `mailto:${app.email}` : undefined}
                    />
                    <Fact
                      label="Phone"
                      value={pro ? app.phone || "Not provided" : maskPhone(app.phone)}
                      href={pro && app.phone ? `tel:${app.phone.replace(/\s+/g, "")}` : undefined}
                    />
                  </div>
                </Locked>
                {!pro ? (
                  <UnlockCandidateCta jobId={jobId} className="mt-4" />
                ) : null}
              </div>

              <div className="rounded-xl border border-purple-strong/10 bg-white p-4">
                <p className={labelClass}>CV</p>
                {pro ? (
                  app.cvName ? (
                    <div className="mt-2.5 flex flex-wrap items-center gap-3">
                      <span className="truncate rounded-xl border border-purple-strong/12 bg-[#f6f8fc] px-3 py-2 text-[13px] font-semibold text-ink/70">
                        {app.cvName}
                      </span>
                      <button type="button" className={ghostClass}>
                        Download
                      </button>
                    </div>
                  ) : (
                    <p className="mt-1.5 text-[13px] text-ink/45">No CV uploaded.</p>
                  )
                ) : (
                  <Locked locked className="mt-2.5">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="truncate rounded-xl border border-purple-strong/12 bg-[#f6f8fc] px-3 py-2 text-[13px] font-semibold text-ink/70">
                        {app.cvName || "candidate-cv.pdf"}
                      </span>
                      <span className={ghostClass}>Download</span>
                    </div>
                  </Locked>
                )}
              </div>

              <PitchPlayer label="60-second pitch" available={app.hasPitch} url={app.audioUrl} className="w-full" />

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => onStatus(app.id, app.status === "Shortlisted" ? "New" : "Shortlisted")}
                  className={app.status === "Shortlisted" ? ghostClass : btnClass}
                >
                  {app.status === "Shortlisted" ? "Remove from shortlist" : "Shortlist"}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-purple-strong/10 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-purple-strong/10 pb-3">
                <p className={labelClass}>Voice assessment</p>
                <span className="text-[12px] font-semibold text-ink/45">
                  {assessmentQuestions.filter((q) => app.assessment?.[q.id]?.url).length} of{" "}
                  {assessmentQuestions.length} answered
                </span>
              </div>
              <div className="mt-4 grid gap-4">
                {assessmentQuestions.map((q, i) => (
                  <div key={q.id} className="rounded-xl border border-purple-strong/10 bg-[#f6f8fc] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-strong">{q.title}</p>
                    <p className="mt-1 min-h-[42px] text-[14px] font-semibold leading-[1.5] text-ink">
                      {i + 1}. {assessmentPrompt(app.assessment?.[q.id], q)}
                    </p>
                    <PitchPlayer
                      label={`Answer ${i + 1}`}
                      available={Boolean(app.assessment?.[q.id]?.url)}
                      url={app.assessment?.[q.id]?.url}
                      emptyLabel="Not answered"
                      className="mt-3 w-full"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Blurs its children and drops a lock badge on top until the employer upgrades. */
function Locked({ locked, children, className = "" }: { locked: boolean; children: React.ReactNode; className?: string }) {
  if (!locked) return <div className={className}>{children}</div>;
  return (
    <div className={`relative overflow-hidden rounded-xl ${className}`}>
      <div className="pointer-events-none select-none blur-[5px] saturate-50" aria-hidden>
        {children}
      </div>
      <div className="absolute inset-0 grid place-items-center bg-white/40">
        <span className="rounded-full border border-purple-strong/20 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-purple-strong shadow-sm">
          Locked
        </span>
      </div>
    </div>
  );
}

function AccountPanel({
  account,
  onSave,
  jobs,
  accessFor,
  countFor,
}: {
  account: EmployerAccount;
  onSave: (a: EmployerAccount) => void;
  jobs: EmployerJob[];
  accessFor: (id: string) => RoleAccessState;
  countFor: (id: string) => number;
}) {
  const [draft, setDraft] = useState<EmployerAccount>(account);
  const [saved, setSaved] = useState(false);
  const set = (patch: Partial<EmployerAccount>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setSaved(false);
  };

  return (
    <div className="grid gap-6">
      <form
        className={panelClass}
        onSubmit={(e) => {
          e.preventDefault();
          onSave(draft);
          setSaved(true);
        }}
      >
        <div className="border-b border-purple-strong/10 px-6 py-5">
          <h2 className="font-serif text-[18px] tracking-[-0.01em] text-ink">Account</h2>
          <p className="mt-1 text-[14px] text-ink/55">
            Your contact details are for us only. They are never shared with candidates.
          </p>
        </div>

        <div className="grid gap-5 px-6 py-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5">
              <span className={labelClass}>Company name</span>
              <input maxLength={100} value={draft.company} onChange={(e) => set({ company: e.target.value })} className={inputClass} />
            </label>
            <label className="grid gap-1.5">
              <span className={labelClass}>Contact name</span>
              <input maxLength={100} value={draft.contactName} onChange={(e) => set({ contactName: e.target.value })} className={inputClass} />
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5">
              <span className={labelClass}>Work email</span>
              <input type="email" maxLength={255} value={draft.email} onChange={(e) => set({ email: e.target.value })} className={inputClass} />
            </label>
            <label className="grid gap-1.5">
              <span className={labelClass}>Phone number</span>
              <input type="tel" maxLength={30} value={draft.phone} onChange={(e) => set({ phone: e.target.value })} className={inputClass} />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button type="submit" className={btnClass}>
              Save details
            </button>
            {saved ? <p className="text-[14px] font-semibold text-purple-strong">Saved.</p> : null}
          </div>
        </div>
      </form>

      <EmployerPasswordSection />
    </div>
  );
}

function EmployerPasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit() {
    setError(null);
    setSaved(false);
    if (next.length < 8) {
      setError("Your new password needs at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("The new passwords don't match.");
      return;
    }
    try {
      await updateEmployerPassword(current, next);
      setSaved(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't update your password.");
    }
  }

  return (
    <section className={panelClass}>
      <div className="border-b border-purple-strong/10 px-6 py-5">
        <h2 className="font-serif text-[18px] tracking-[-0.01em] text-ink">Password</h2>
        <p className="mt-1 text-[14px] text-ink/55">Update the password you use to log in.</p>
      </div>
      <div className="grid gap-5 px-6 py-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="grid gap-1.5">
            <span className={labelClass}>Current password</span>
            <PasswordInput
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={`${inputClass} pr-10`}
              iconClassName="absolute inset-y-0 right-3 flex items-center text-ink/40 hover:text-ink/70"
              autoComplete="current-password"
            />
          </label>
          <label className="grid gap-1.5">
            <span className={labelClass}>New password</span>
            <PasswordInput
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={`${inputClass} pr-10`}
              iconClassName="absolute inset-y-0 right-3 flex items-center text-ink/40 hover:text-ink/70"
              autoComplete="new-password"
            />
          </label>
          <label className="grid gap-1.5">
            <span className={labelClass}>Confirm new password</span>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={`${inputClass} pr-10`}
              iconClassName="absolute inset-y-0 right-3 flex items-center text-ink/40 hover:text-ink/70"
              autoComplete="new-password"
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <button type="button" onClick={() => void submit()} className={btnClass}>
            Update password
          </button>
          {error ? <span className="text-[14px] font-semibold text-ink/70">{error}</span> : null}
          {saved ? <span className="text-[14px] font-semibold text-purple-strong">Password updated.</span> : null}
        </div>
      </div>
    </section>
  );
}

function Fact({ label, value, href }: { label: string; value: string; href?: string | undefined }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-purple-strong/[0.07] pb-2 last:border-0 last:pb-0">
      <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-ink/40">{label}</span>
      {href ? (
        <a href={href} className="truncate text-[14px] font-semibold text-purple-strong hover:underline">
          {value}
        </a>
      ) : (
        <span className="truncate text-[14px] text-ink/75">{value}</span>
      )}
    </div>
  );
}

function PitchPlayer({
  label,
  available,
  url,
  emptyLabel = "No pitch recorded",
  className = "",
}: {
  label: string;
  available: boolean;
  /** Real, playable recording URL. May be absent even when `available` is true — see AddAudioUrlToApplicationsTable. */
  url?: string | undefined;
  emptyLabel?: string;
  className?: string;
}) {
  return (
    <div
      className={`flex h-[82px] min-w-0 flex-col justify-between overflow-hidden rounded-xl border border-purple-strong/10 bg-[#f6f8fc] px-3.5 py-2.5 ${className}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45">{label}</span>
        {!available ? (
          <span className="shrink-0 text-[11px] font-semibold text-ink/35">{emptyLabel}</span>
        ) : !url ? (
          <span className="shrink-0 text-[11px] font-semibold text-ink/35">Recording unavailable</span>
        ) : null}
      </div>
      {available && url ? (
        <audio controls src={url} className="block h-8 min-h-8 w-full max-w-full" />
      ) : (
        <div className="h-8 w-full rounded-md border border-dashed border-purple-strong/15" />
      )}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <p className="rounded-md border border-dashed border-purple-strong/15 px-6 py-8 text-center text-[15px] text-ink/50">{text}</p>
  );
}

/**
 * A job always starts status="Live" the moment it's posted, but that only
 * means the employer switched it on — CLOSDD still has to approve it before
 * it's actually visible on the public board (see GET /api/public/jobs,
 * which requires decision="Approved"). `decision` is only optional on the
 * type for old client-side demo/seed jobs that never had a review step; a
 * real job from the API always carries one.
 */
function jobStatusLabel(job: EmployerJob): string {
  if (job.status === "Live" && job.decision !== undefined && job.decision !== "Approved") {
    return job.decision === "Rejected" ? "Not approved" : "Pending approval";
  }
  return job.status;
}

function StatusPill({ status }: { status: string }) {
  const s = status.toLowerCase();
  const tone = s.includes("live")
    ? "border-tag-green/25 bg-tag-green-soft text-tag-green"
    : s.includes("shortlist")
      ? "border-purple-strong/20 bg-purple-soft text-purple-strong"
      : s.includes("new")
        ? "border-tag-amber/25 bg-tag-amber-soft text-tag-amber"
        : s.includes("reject")
          ? "border-tag-red/20 bg-tag-red-soft text-tag-red"
          : "border-purple-strong/15 bg-ink/[0.04] text-ink/55";
  return (
    <span className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${tone}`}>
      {status}
    </span>
  );
}
